import {
  ItemMetaType,
  ProductItemStateType,
  Variant,
} from '@packages/systems/commerce/core';
import {CMS_STATES} from './constants';
import {type CommerceStateShape} from './types';
import {createSelector} from 'reselect';
import {List} from 'immutable';
import {isSkusCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {selectCurrentLocale} from '@packages/systems/localization';
import {CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import {LocalizationState} from '@packages/systems/localization-types';

export function didTheUserSetUpCommerceForThisSite(
  commerceState: CommerceStateShape
) {
  return commerceState.status
    ? commerceState.status.cmsState === CMS_STATES.READY
    : false;
}

export function didTheUserEnableCheckoutForThisSite(
  commerceState: Pick<CommerceStateShape, 'settings'>
) {
  return commerceState.settings.savedCheckoutEnabled;
}

export const getProductQuantity = (
  state: ProductItemStateType,
  isInlineItem: boolean
) => {
  const itemMeta = isInlineItem
    ? state.currentInlineItemMeta
    : state.currentItemMeta;
  return itemMeta.quantity;
};

export const getProductInventoryType = (
  state: ProductItemStateType,
  isInlineItem: boolean
) => {
  const itemMeta = isInlineItem
    ? state.currentInlineItemMeta
    : state.currentItemMeta;
  return itemMeta.inventoryType;
};

export const isItemMetaSet = (itemMeta: ItemMetaType): boolean =>
  itemMeta.variants.size > 0;

export const selectAllVariantsFromCollection = createSelector(
  (state: {CollectionStore: CollectionStateType}) => state.CollectionStore,
  (state: {CollectionStore: CollectionStateType}) => {
    if (!state.CollectionStore) {
      return null;
    }
    return state.CollectionStore.collections.find(isSkusCollection);
  },
  (state: {LocalizationStore: LocalizationState}) =>
    state.LocalizationStore
      ? selectCurrentLocale(state.LocalizationStore)
      : null,
  (collectionState, skuCollection, currentLocale) => {
    if (!collectionState || !skuCollection) {
      return List<Variant>();
    }

    return collectionState
      .itemsFor(
        skuCollection,
        currentLocale ? {locale: currentLocale} : undefined
      )
      .map((item) => item.toJS() as Variant);
  }
);

export const selectDefaultCurrency = createSelector(
  (state: {CommerceStore: CommerceStateShape}) => state.CommerceStore.settings,
  (settings) => settings.defaultCurrency
);
